import { pdf } from "@react-pdf/renderer";
import { PDFDocument } from "pdf-lib";
import { supabase } from "../lib/supabase";
import { ShippingLabelDocument } from "../components/ShippingLabel";
import { ProductWithBarcode } from "../components/ShippingLabel";
import JsBarcode from "jsbarcode";

interface Booking {
  id: number;
  reference: string;
  transport_type: { name: string; abbreviation: string };
  booking_type?: { name: string };
  booking_incoterm: { name: string };
  origin_port?: { name: string; country: string };
  destination_port?: { name: string; country: string };
  supplier: {
    name: string;
    email: string;
    phone: string;
    country: string;
    city: string;
    address: string;
  };
}

interface Product {
  id: number;
  name: string;
  hs_code: string | null;
  gross_weight: number;
  cbm: number;
  pieces?: number | null;
  cartons?: number | null;
  ean: number | null;
  company_id: number;
  shipping_label?: string | null;
}

function generateBarcodeBase64(ean: number): string | null {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 800;
    canvas.height = 200;

    const barcodeValue = ean.toString();

    JsBarcode(canvas, barcodeValue, {
      format: "CODE128",
      width: 2,
      height: 100,
      displayValue: false,
      fontSize: 20,
      margin: 10,
      background: "#ffffff",
      lineColor: "#000000",
      flat: true,
      textAlign: "center",
      textPosition: "bottom",
      textMargin: 6,
    });

    return canvas.toDataURL("image/png", 1.0);
  } catch (err) {
    console.error("Error generating barcode:", err);
    return null;
  }
}

/** Generate a shipping label PDF blob (does not upload). */
export const generateShippingLabelPdfBlob = async (
  booking: Booking,
  product: Product,
  companyName: string
): Promise<Blob | null> => {
  try {
    const barcodeBase64 = product.ean ? generateBarcodeBase64(product.ean) : null;

    const productWithBarcode: ProductWithBarcode = {
      ...product,
      gross_weight: product.gross_weight
        ? Number(product.gross_weight.toFixed(2))
        : 0,
      cbm: product.cbm ? Number(product.cbm.toFixed(2)) : 0,
      barcodeBase64,
    };

    return await pdf(
      ShippingLabelDocument({
        booking,
        product: productWithBarcode,
        companyName,
      })
    ).toBlob();
  } catch (error) {
    console.error("Error generating shipping label blob:", error);
    return null;
  }
};

async function uploadShippingLabelBlob(
  blob: Blob,
  booking: Booking,
  product: Product
): Promise<string | null> {
  try {
    const base64Data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });

    const timestamp = Date.now();
    const filePath = `${product.company_id}/${booking.id}/booking_${booking.id}_product_${product.id}_${booking.transport_type?.abbreviation || ""}_shipping_label_${timestamp}.pdf`;

    const { error: uploadError } = await supabase.storage
      .from("shipping_labels")
      .upload(filePath, decode(base64Data), {
        contentType: "application/pdf",
        upsert: true,
      });

    if (uploadError) throw uploadError;
    return filePath;
  } catch (uploadError) {
    console.error("Error uploading shipping label:", uploadError);
    return null;
  }
}

export const generateAndSaveShippingLabel = async (
  booking: Booking,
  product: Product,
  companyName: string
): Promise<string | null> => {
  try {
    const blob = await generateShippingLabelPdfBlob(
      booking,
      product,
      companyName
    );
    if (!blob) return null;
    return uploadShippingLabelBlob(blob, booking, product);
  } catch (error) {
    console.error("Error generating shipping label:", error);
    return null;
  }
};

async function fetchStoredLabelBlob(
  shippingLabelPath: string
): Promise<Blob | null> {
  try {
    const { data, error } = await supabase.storage
      .from("shipping_labels")
      .createSignedUrl(shippingLabelPath, 3600);
    if (error || !data?.signedUrl) return null;

    const response = await fetch(data.signedUrl);
    if (!response.ok) return null;
    return await response.blob();
  } catch (error) {
    console.error("Error fetching stored shipping label:", error);
    return null;
  }
}

/**
 * Build one PDF with each product label repeated by carton count.
 * Missing labels are generated (and saved) on the fly.
 */
export const downloadCombinedProductLabelsPdf = async (params: {
  booking: Booking;
  products: Product[];
  companyName: string;
  /** When true, each product label is repeated by carton count. When false, one page per product. */
  repeatByCartons?: boolean;
  onProgress?: (current: number, total: number) => void;
}): Promise<{
  blob: Blob;
  pageCount: number;
  productUpdates: Array<{ id: number; shipping_label: string }>;
} | null> => {
  const {
    booking,
    products,
    companyName,
    repeatByCartons = true,
    onProgress,
  } = params;
  const pdfDoc = await PDFDocument.create();
  const productUpdates: Array<{ id: number; shipping_label: string }> = [];

  let processed = 0;
  for (const product of products) {
    const copies = repeatByCartons
      ? Math.max(Number(product.cartons) || 0, 1)
      : 1;

    let labelBlob: Blob | null = null;
    if (product.shipping_label) {
      labelBlob = await fetchStoredLabelBlob(product.shipping_label);
    }

    if (!labelBlob) {
      labelBlob = await generateShippingLabelPdfBlob(
        booking,
        product,
        companyName
      );
      if (labelBlob) {
        const path = await uploadShippingLabelBlob(labelBlob, booking, product);
        if (path) {
          productUpdates.push({ id: product.id, shipping_label: path });
          const { error } = await supabase
            .from("booking_product")
            .update({ shipping_label: path })
            .eq("id", product.id);
          if (error) {
            console.error("Failed to persist shipping_label path:", error);
          }
        }
      }
    }

    if (labelBlob) {
      try {
        const labelPdf = await PDFDocument.load(await labelBlob.arrayBuffer());
        const pageIndices = labelPdf.getPageIndices();
        for (let i = 0; i < copies; i++) {
          const pages = await pdfDoc.copyPages(labelPdf, pageIndices);
          pages.forEach((page) => pdfDoc.addPage(page));
        }
      } catch (error) {
        console.error(`Error merging label for product ${product.id}:`, error);
      }
    }

    processed++;
    onProgress?.(processed, products.length);
  }

  if (pdfDoc.getPageCount() === 0) {
    return null;
  }

  const pdfBytes = await pdfDoc.save();
  const buffer = new ArrayBuffer(pdfBytes.length);
  const view = new Uint8Array(buffer);
  view.set(pdfBytes);

  return {
    blob: new Blob([buffer], { type: "application/pdf" }),
    pageCount: pdfDoc.getPageCount(),
    productUpdates,
  };
};

// Helper function to decode base64
function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export function generateRegenerateLabelUrl(
  productName: string,
  pieces: number,
  cartons: number,
  weight: number,
  cbm: number,
  ean: string,
  bookingId: number,
  productId: number,
  companyId: number,
  companyName: string,
  baseUrl: string = window.location.origin
): string {
  const data = {
    productName,
    pieces,
    cartons,
    weight,
    cbm,
    ean,
    bookingId,
    productId,
    companyId,
    companyName,
  };

  // Encode the data as base64
  const encodedData = btoa(JSON.stringify(data));

  // Generate a hash using the secret key
  const secret = process.env.REACT_APP_LABEL_SECRET || "your-secret-key";
  const hash = btoa(encodedData + secret);

  // Construct the URL
  return `${baseUrl}/regenerate-label?data=${encodedData}&hash=${hash}`;
}
